import { GenericDropdownOption } from '../../../../utils/utils.types';
import {
    CampaignType,
    CampaignManagerCampaign,
    getCampaignManagerCampaigns,
    getCampaignManagerClients,
    getCampaignManagerClientUsers,
    getCampaignManagerInfluencers,
    postCampaignManagerBooking,
    postCampaignManagerCampaign,
    postCampaignManagerClientUser,
    ApexClientUser,
    PostType,
} from '@round/api';
import CampaignSelectOption, { CampaignOption } from '../../../booking/CampaignOption/CampaignOption';
import React, { useEffect, useState } from 'react';
import useNonNullContext from '../../../../hooks/useNonNullContext';
import { OptionsContext } from '../../../../contexts/OptionsContext';
import { useSelectPaginatedOptions } from '../../../../hooks/useSelectPaginatedOptions';
import { ErrorMessage, Field, FieldProps, Form, Formik, FormikHelpers } from 'formik';
import { mapApiErrorsToFormikErrors } from '../../../../utils/utits';
import { showNotification } from '../../../../utils/showNotification';
import * as yup from 'yup';
import omit from 'lodash/omit';
import { FormField, Label, Modal, ModalTitle, Select } from '@round/ui-kit';
import styles from './CreateCampaignManagerBookingModal.module.css';
import capitalize from 'lodash/capitalize';
import Button from '../../../../ui/Button/Button';
import SectionTitle from '../../../../ui/SectionTitle/SectionTitle';
import { postTypeOptions } from '../../../booking/constants';

type CreateCampaignManagerBookingModalProps = {
    isOpen: boolean;
    onClose: () => void;
    onCreated: (bookingId: number) => void;
};

type FormValues = {
    influencer_id: GenericDropdownOption<any> | null;
    post_type: GenericDropdownOption<PostType> | null;
    cost: number | undefined;
    campaign_id: CampaignOption | null;
    // fields on campaign create
    name: string;
    currency: GenericDropdownOption<number> | null;
    client_user: (GenericDropdownOption<number> & Pick<ApexClientUser, 'email' | 'client_name'>) | null;
    type: GenericDropdownOption<CampaignType> | null;
    // fields on booker create
    client: GenericDropdownOption<number> | null;
    email: string;
};

const initialValues: FormValues = {
    influencer_id: null,
    client_user: null,
    post_type: null,
    cost: undefined,
    campaign_id: null,
    name: '',
    currency: null,
    type: null,
    client: null,
    email: '',
};

const campaignTypeOptions: GenericDropdownOption<CampaignType>[] = [
    { value: 'AUDIO', label: 'Audio' },
    { value: 'BRAND', label: 'Brand' },
];

type FormModes = 'select-campaign' | 'create-campaign' | 'create-booker';
const CreateCampaignManagerBookingModal = ({ onCreated, onClose, isOpen }: CreateCampaignManagerBookingModalProps) => {
    const [formMode, setFormMode] = useState<FormModes>('select-campaign');
    const { currencies } = useNonNullContext(OptionsContext);

    useEffect(() => {
        if (!isOpen) {
            setFormMode('select-campaign');
        }
    }, [isOpen]);

    const { props: creatorSelectProps } = useSelectPaginatedOptions<
        GenericDropdownOption<number>,
        typeof getCampaignManagerInfluencers
    >({
        fetchOptions: getCampaignManagerInfluencers,
        mapToOption: (item) => ({ value: item.id, label: item.name }),
    });

    const { props: campaignSelectProps, reset: resetCampaignSelectOptions } = useSelectPaginatedOptions<
        NonNullable<FormValues['campaign_id']>,
        typeof getCampaignManagerCampaigns
    >({
        fetchOptions: getCampaignManagerCampaigns,
        mapToOption: (item) => ({
            value: item.id,
            label: item.name,
            client_user_email: item.client_user_email,
            client_name: item.client_name,
            currency: item.currency,
            type: item.type,
        }),
    });

    const { props: bookerSelectProps } = useSelectPaginatedOptions<
        NonNullable<FormValues['client_user']>,
        typeof getCampaignManagerClientUsers
    >({
        fetchOptions: getCampaignManagerClientUsers,
        mapToOption: (item) => ({
            value: item.id,
            label: item.email,
            email: item.email,
            client_name: item.client_name,
        }),
    });

    const { props: clientSelectProps, reset: resetClientSelectOptions } = useSelectPaginatedOptions({
        fetchOptions: getCampaignManagerClients,
        mapToOption: (item) => ({ value: item.id, label: item.name }),
    });

    const handleSubmit = async (values: FormValues, helpers: FormikHelpers<FormValues>) => {
        try {
            helpers.setSubmitting(true);
            let bookerId = values.client_user?.value;
            if (!bookerId && formMode === 'create-booker') {
                const response = await postCampaignManagerClientUser({
                    email: values.email,
                    client: values.client?.value!,
                });

                if (response.status === 400) {
                    helpers.setErrors(mapApiErrorsToFormikErrors(response.data));
                    return;
                }

                bookerId = response.data.id;
                resetClientSelectOptions();
            }

            let campaign: FormValues['campaign_id'] | CampaignManagerCampaign = values.campaign_id;
            if (!campaign && formMode !== 'select-campaign') {
                const response = await postCampaignManagerCampaign({
                    type: values.type?.value!,
                    name: values.name,
                    currency: values.currency?.value!,
                    client_user: bookerId!,
                });

                if (response.status === 400) {
                    helpers.setErrors(mapApiErrorsToFormikErrors(response.data));
                    return;
                }

                campaign = response.data;
                resetCampaignSelectOptions();
            }

            const response = await postCampaignManagerBooking({
                post_type: values.post_type?.value,
                campaign_id:
                    typeof (campaign as FormValues['campaign_id'])?.value === 'number'
                        ? (campaign as FormValues['campaign_id'])?.value
                        : (campaign as CampaignManagerCampaign)?.id,
                cost: values.cost?.toString(),
                influencer_id: values.influencer_id?.value,
            });

            if (response.status === 400) {
                helpers.setErrors(mapApiErrorsToFormikErrors(response.data));
                return;
            }

            showNotification('Booking created', 'info');
            onCreated(response.data.id);
        } catch {
            showNotification('Could not create booking', 'error');
        } finally {
            helpers.setSubmitting(false);
        }
    };

    const baseValidationSchema = {
        influencer_id: yup.object().label('Creator').nullable().required(),
        post_type: yup.object().label('Post Type').nullable().required(),
        cost: yup.number().label('Price').required(),
    };

    const campaignSelectValidationSchema = {
        ...baseValidationSchema,
        campaign_id: yup.object().label('Campaign').nullable().required(),
    };

    const createCampaignValidationSchema = {
        ...baseValidationSchema,
        name: yup.string().label('Campaign name').required(),
        currency: yup.object().label('Currency').nullable().required(),
        type: yup.object().label('Campaign type').nullable().required(),
        client_user: yup.object().label('Booker').nullable().required(),
    };

    const createBookerValidationSchema = {
        ...omit(createCampaignValidationSchema, ['client_user']),
        client: yup.object().label('Client').nullable().required(),
        email: yup.string().label('Email').required(),
    };

    const validationSchemas: { [key in FormModes]: any } = {
        'select-campaign': campaignSelectValidationSchema,
        'create-campaign': createCampaignValidationSchema,
        'create-booker': createBookerValidationSchema,
    };

    return (
        <Modal className={styles.modal} isOpen={isOpen} onClose={onClose} closeOnOverlayClick>
            <Formik
                enableReinitialize
                initialValues={initialValues}
                onSubmit={handleSubmit}
                validationSchema={yup.object(validationSchemas[formMode])}
            >
                {({ submitForm, values, isSubmitting, dirty, isValid }) => {
                    const client =
                        formMode === 'select-campaign'
                            ? values.campaign_id?.client_name || '-'
                            : formMode === 'create-campaign'
                              ? values.client_user?.client_name || '-'
                              : values.client?.label || '-';

                    const booker =
                        formMode === 'select-campaign'
                            ? values.campaign_id?.client_user_email || '-'
                            : formMode === 'create-campaign'
                              ? values.client_user?.email || '-'
                              : values?.email || '-';

                    const campaignType =
                        formMode === 'select-campaign'
                            ? capitalize(values.campaign_id?.type) || '-'
                            : values.type?.label || '-';

                    return (
                        <>
                            <div className={styles.header}>
                                <ModalTitle>New Booking</ModalTitle>
                                <Button
                                    className={styles.submit}
                                    onClick={submitForm}
                                    loading={isSubmitting}
                                    disabled={!dirty || !isValid}
                                >
                                    Create
                                </Button>
                            </div>
                            <SectionTitle>Campaign Details</SectionTitle>

                            <div className={styles.formContainer}>
                                <Form className={styles.form}>
                                    <div className={styles.bookingDetails}>
                                        <FormField>
                                            <Label htmlFor="influencer_id">Creator</Label>
                                            <Field name="influencer_id">
                                                {(props: FieldProps<FormValues['influencer_id'], FormValues>) => (
                                                    <Select
                                                        {...creatorSelectProps}
                                                        placeholder="Select creator"
                                                        value={props.field.value}
                                                        onChange={(value) =>
                                                            props.form.setFieldValue(props.field.name, value)
                                                        }
                                                        onBlur={() => props.form.setFieldTouched(props.field.name)}
                                                    />
                                                )}
                                            </Field>
                                            <ErrorMessage name="influencer_id">
                                                {(msg) => <span className={styles.errorMessage}>{msg}</span>}
                                            </ErrorMessage>
                                        </FormField>

                                        <FormField>
                                            <Label htmlFor="post_type">Post Type</Label>
                                            <Field name="post_type">
                                                {(props: FieldProps<FormValues['post_type'], FormValues>) => (
                                                    <Select
                                                        placeholder="Select post type"
                                                        options={postTypeOptions}
                                                        value={props.field.value}
                                                        onChange={(value) =>
                                                            props.form.setFieldValue(props.field.name, value)
                                                        }
                                                        onBlur={() => props.form.setFieldTouched(props.field.name)}
                                                    />
                                                )}
                                            </Field>
                                            <ErrorMessage name="post_type">
                                                {(msg) => <span className={styles.errorMessage}>{msg}</span>}
                                            </ErrorMessage>
                                        </FormField>

                                        <FormField>
                                            <Label htmlFor="cost">Booking Price</Label>
                                            <div className={styles.priceInputContainer}>
                                                <span className={styles.priceInputCurrencySign}>
                                                    {currencies.find((currency) => {
                                                        if (formMode !== 'select-campaign') {
                                                            return currency.id === values.currency?.value;
                                                        }

                                                        return currency.id === values.campaign_id?.currency;
                                                    })?.symbol ?? '-'}
                                                </span>
                                                <Field id="cost" name="cost" type="number" placeholder="Enter price" />
                                            </div>
                                            <ErrorMessage name="cost">
                                                {(msg) => <span className={styles.errorMessage}>{msg}</span>}
                                            </ErrorMessage>
                                        </FormField>
                                    </div>

                                    {formMode === 'select-campaign' && (
                                        <FormField>
                                            <Label htmlFor="campaign_id">Campaign</Label>
                                            <Field name="campaign_id">
                                                {(props: FieldProps<FormValues['campaign_id'], FormValues>) => (
                                                    <Select
                                                        {...campaignSelectProps}
                                                        placeholder="Select campaign"
                                                        value={props.field.value}
                                                        onChange={(value) =>
                                                            props.form.setFieldValue(props.field.name, value)
                                                        }
                                                        onBlur={() => props.form.setFieldTouched(props.field.name)}
                                                        components={{ Option: CampaignSelectOption }}
                                                    />
                                                )}
                                            </Field>
                                            <ErrorMessage name="campaign_id">
                                                {(msg) => <span className={styles.errorMessage}>{msg}</span>}
                                            </ErrorMessage>
                                        </FormField>
                                    )}

                                    {formMode !== 'select-campaign' && (
                                        <>
                                            <FormField>
                                                <Label htmlFor="name">Campaign Name</Label>
                                                <Field name="name" id="name" placeholder="Enter campaign name" />
                                                <ErrorMessage name="name">
                                                    {(msg) => <span className={styles.errorMessage}>{msg}</span>}
                                                </ErrorMessage>
                                            </FormField>

                                            <div className={styles.equalRow}>
                                                <FormField>
                                                    <Label htmlFor="currency">Currency</Label>
                                                    <Field name="currency">
                                                        {(props: FieldProps<FormValues['currency'], FormValues>) => (
                                                            <Select
                                                                options={currencies.map((c) => ({
                                                                    value: c.id,
                                                                    label: c.name,
                                                                }))}
                                                                placeholder="Select currency"
                                                                value={props.field.value}
                                                                onChange={(value) =>
                                                                    props.form.setFieldValue(props.field.name, value)
                                                                }
                                                                onBlur={() =>
                                                                    props.form.setFieldTouched(props.field.name)
                                                                }
                                                            />
                                                        )}
                                                    </Field>
                                                    <ErrorMessage name="currency">
                                                        {(msg) => <span className={styles.errorMessage}>{msg}</span>}
                                                    </ErrorMessage>
                                                </FormField>

                                                <FormField>
                                                    <Label htmlFor="type">Campaign Type</Label>
                                                    <Field name="type">
                                                        {(props: FieldProps<FormValues['type'], FormValues>) => (
                                                            <Select
                                                                placeholder="Select type"
                                                                options={campaignTypeOptions}
                                                                value={props.field.value}
                                                                onChange={(value) =>
                                                                    props.form.setFieldValue(props.field.name, value)
                                                                }
                                                                onBlur={() =>
                                                                    props.form.setFieldTouched(props.field.name)
                                                                }
                                                            />
                                                        )}
                                                    </Field>
                                                    <ErrorMessage name="type">
                                                        {(msg) => <span className={styles.errorMessage}>{msg}</span>}
                                                    </ErrorMessage>
                                                </FormField>
                                            </div>

                                            {formMode === 'create-campaign' && (
                                                <FormField>
                                                    <Label htmlFor="client_user">Booker</Label>
                                                    <Field name="client_user">
                                                        {(props: FieldProps<FormValues['client_user'], FormValues>) => (
                                                            <Select
                                                                {...bookerSelectProps}
                                                                placeholder="Select booker"
                                                                value={props.field.value}
                                                                onChange={(value) =>
                                                                    props.form.setFieldValue(props.field.name, value)
                                                                }
                                                                onBlur={() =>
                                                                    props.form.setFieldTouched(props.field.name)
                                                                }
                                                            />
                                                        )}
                                                    </Field>
                                                    <ErrorMessage name="client_user">
                                                        {(msg) => <span className={styles.errorMessage}>{msg}</span>}
                                                    </ErrorMessage>
                                                </FormField>
                                            )}

                                            {formMode === 'create-booker' && (
                                                <div className={styles.equalRow}>
                                                    <FormField>
                                                        <Label htmlFor="client">Booker Client</Label>
                                                        <Field name="client">
                                                            {(props: FieldProps<FormValues['client'], FormValues>) => (
                                                                <Select
                                                                    {...clientSelectProps}
                                                                    placeholder="Select booker client"
                                                                    value={props.field.value}
                                                                    onChange={(value) =>
                                                                        props.form.setFieldValue(
                                                                            props.field.name,
                                                                            value
                                                                        )
                                                                    }
                                                                    onBlur={() =>
                                                                        props.form.setFieldTouched(props.field.name)
                                                                    }
                                                                />
                                                            )}
                                                        </Field>
                                                        <ErrorMessage name="client">
                                                            {(msg) => (
                                                                <span className={styles.errorMessage}>{msg}</span>
                                                            )}
                                                        </ErrorMessage>
                                                    </FormField>

                                                    <FormField>
                                                        <Label htmlFor="email">Booker Email</Label>
                                                        <Field
                                                            name="email"
                                                            id="email"
                                                            placeholder="Enter booker email"
                                                        />
                                                        <ErrorMessage name="email">
                                                            {(msg) => (
                                                                <span className={styles.errorMessage}>{msg}</span>
                                                            )}
                                                        </ErrorMessage>
                                                    </FormField>
                                                </div>
                                            )}
                                        </>
                                    )}

                                    {formMode !== 'select-campaign' && (
                                        <button
                                            type="button"
                                            className={styles.switchCampaignFormButton}
                                            onClick={() =>
                                                setFormMode((mode) =>
                                                    mode === 'create-campaign' ? 'create-booker' : 'create-campaign'
                                                )
                                            }
                                        >
                                            {formMode === 'create-campaign' ? 'Create Booker' : 'Select Booker'}
                                        </button>
                                    )}

                                    <button
                                        type="button"
                                        className={styles.switchCampaignFormButton}
                                        onClick={() =>
                                            setFormMode((mode) =>
                                                mode !== 'select-campaign' ? 'select-campaign' : 'create-campaign'
                                            )
                                        }
                                    >
                                        {formMode !== 'select-campaign' ? 'Select Campaign' : 'Create Campaign'}
                                    </button>
                                </Form>

                                <div className={styles.campaignInfo}>
                                    <div>
                                        <p className={styles.campaignInfoLabel}>Client</p>
                                        <p className={styles.campaignInfoValue}>{client}</p>
                                    </div>

                                    <div>
                                        <p className={styles.campaignInfoLabel}>Booker</p>
                                        <p className={styles.campaignInfoValue}>{booker}</p>
                                    </div>

                                    <div>
                                        <p className={styles.campaignInfoLabel}>Campaign Type</p>
                                        <p className={styles.campaignInfoValue}>{campaignType}</p>
                                    </div>
                                </div>
                            </div>
                        </>
                    );
                }}
            </Formik>
        </Modal>
    );
};

export default CreateCampaignManagerBookingModal;
